import React, { JSX, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BsFillPauseFill, BsFillPlayFill, BsFilm, BsListUl } from 'react-icons/bs';
import styled, { css } from 'styled-components';
import { GrRedo, GrRevert } from "react-icons/gr";
import { theme } from '../../styles/theme';
import { FiFileText, FiMusic, FiPause, FiPlay } from 'react-icons/fi';
import AudioVisualizer from '../../components/AudioVisualizer';
import MicrophoneVisualizer from '../../components/MicrophoneVisualizer';
import Timeline from '../../components/Timeline';
import useVideo from '../../hooks/useVideo';
import VideoTrimmer from './VideoTrimmer';
import ReactPlayer from 'react-player';
import Thumbnails from './Thumbnails';
import { formatTime } from 'src/shared/utils/helper';
import { trimTime } from 'src/shared/utils/record-functions';
import { ProgressContext } from 'src/ProgressContext';
import VideoProcessingUtility from 'src/shared/utils/VideoProcessingUtility';
import VideoTrimerV2 from 'src/components/VideoTrimerV2';
import useVideoTrim from 'src/hooks/useVideoTrim';
import VideoThumbnails from './VideoThumbnails';
import { useMediaQuery } from 'react-responsive';



// Styled Components
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  padding-bottom: 0px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #F2F2F4;
  width: 100%;
  height: 60px;
`;

const IconBox = styled.div<{ align: 'start' | 'center' | 'end' }>`
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  
`

const IconButton = styled.button`
  background: transparent;
  border: none;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-right: 8px;
  border-radius: 4px;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

const TimelineContainer = styled.div`
  width: 100%;
  height: 28px;
  gap: 10px;

  display: flex;
  flex-direction: row;
  flex: 1;
  overflow-x: auto;
`;

const TimelineRuler = styled.div`
  height: 24px;
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
  position: relative;
`;

const TimeMarker = styled.div`
  position: absolute;
  width: 1px;
  height: 8px;
  background-color: #aaa;
  bottom: 0;
  font-size: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimeText = styled.span`
  color: #666;
  font-size: 10px;
  margin-top: -18px;
`;

const TracksArea = styled.div`
  width: 100%;
  height: 214px;
  gap: 12px;
  padding-top: 12px;
  padding-bottom: 32px;
  overflow: hidden;

  background: #F2F2F4;
  display: flex;
  flex: 1;
  overflow-y: auto;
  align-item: center;
  justify-content: start;
  @media (max-width: ${theme.breakpoints.tablet}px) {
    display:none
  }
`;

const Sidebar = styled.div`
  width: 64px;
  height: 170px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  // background-color: white;
  // border-right: 1px solid #e0e0e0;
`;

const ToolButton = styled.button<{ active?: boolean }>`
  width: 64px;
  height: 35px;
  border-radius: 8px;

  border-radius: 15px;
  border: none;
  background-color: transparent;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  

  ${props=>props.active && css`
    background: #00000014;
  `}
`;

const IntroButton= styled.div`
  width: 88px;
  height: 64px;
  gap: 10px;
  border-radius: 12px;
  border-width: 1px;
  padding: 8px;
  background: #F4F4F4;
  border: 1px solid #8080804D;

  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: end;

  &>span{
    width: 38px;
    height: 22px ;
    gap: 10px;
    border-radius: 8px;
    padding: 4px 6px;
    background: #0000001A;

    font-family: ${theme.fonts.primary};
    font-weight: ${theme.fontWeights.medium};
    font-size: ${theme.fontSizes.sm};
    line-height: 100%;
    letter-spacing: 0%;

  }
`;

const OuttroButton = styled(IntroButton)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 2px;

  
  border-radius: 12px;
  padding: 8px;
  padding-left: 10px;
  border-width: 1px;


  &>span{
  width: auto;
    background: transparent;
    border: 0px;
    color: #949495;
  }

`;

const SidebarItem = styled.div`
  padding: 8px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e0e0e0;
`;

const Tracks = styled.div`
  max-width: calc(100% - 64px);
  height: 100%;
  display: flex;
  flex-direction: row;
  gap: 12px;
  overflow: hidden;
  padding: 0px 20px;
  flex-wrap: wrap;
`;

const Track = styled.div`
  height: 64px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  position: relative;
  align-items: center;
`;



const AudioWaveform = styled.div`
  height: 16px;
  background: #e0e0e0;
  margin-left: 8px;
  border-radius: 2px;
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 100%;
    background: linear-gradient(90deg, 
      rgba(200, 200, 200, 0.8) 0%, 
      rgba(200, 200, 200, 0.2) 10%,
      rgba(200, 200, 200, 0.8) 20%,
      rgba(200, 200, 200, 0.4) 30%,
      rgba(200, 200, 200, 0.6) 40%,
      rgba(200, 200, 200, 0.8) 50%,
      rgba(200, 200, 200, 0.4) 60%,
      rgba(200, 200, 200, 0.6) 70%,
      rgba(200, 200, 200, 0.2) 80%,
      rgba(200, 200, 200, 0.8) 90%,
      rgba(200, 200, 200, 0.5) 100%
    );
  }
`;

const AddButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #6200ee;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  cursor: pointer;
  
  &:hover {
    background-color: #5000d6;
  }
`;

const PlayButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #6200ee;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0 12px;
  &:hover {
    background-color: #5000d6;
  }
  
`;

const TimeDisplay = styled.div`
  font-familty: ${theme.fonts.primary}
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.regular};
  margin-right: 4px;

  color: #A3A1A2; 

  &>span{
    color: #000;
  } 
`;

const TrackItemThumbnail = styled.div`
  height: 64px;
  width: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  img{
    width: 64px;
    height: 64px;
  }
`;

interface TimelineProps{
  index: number;
  videos: any[];
  progress?: number;
  ffmpeg: any;
  isPlayerReady: boolean;
  onVideoTap: (index: number) => void;
  onTrimmingChange: (blob: Blob | null,positions:{start:number,end:number}) => void;
  onThumbNail: (frame: any) => void;
  allVideoDuration: number;
  onPlay: (value: boolean) => void;
}
// Main Component
const VideoTimelineEditor: React.FC<TimelineProps> = ({index, videos, isPlayerReady=false, progress=0, allVideoDuration, onTrimmingChange, onThumbNail, onVideoTap, onPlay}) => {

  const [thumbnails, setThumbnails] = useState<Record<number, JSX.Element | null>>({});
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const isNotPC = useMediaQuery({ maxWidth: theme.breakpoints.tablet });
  const [totalDuration, setTotalDuration] = useState(60);
  const [totalPlayedDuration, setTotalPlayedDuration] = useState<number>(0);

  const { progressTracker } = useContext(ProgressContext);
  const {isScriptLoaded, handleTrimVideo, getFramesV2}=useVideoTrim()


  // const totalDuration = 60; // 60 seconds
  const pixelsPerSecond = 100;
  const totalWidth = totalDuration * pixelsPerSecond;

  const {loading, getFrames, trimVideo} = useVideo();
  const [frames, setFrames,] = useState([]);
  
  const calculateTotalDuration = () => {
    return videos.reduce((total, video) => {
      let dura=0
      if ( video.duration && isFinite(video.duration) && !isNaN(video.duration)){
        dura=video.duration
      }
      return total + dura
    }, 0);
  };

  const calculateCounterPosition = (index: number) => {
    if (videos.length === 0) return 0;
    if (index === 0) return 0;
    
    const totalDuration = calculateTotalDuration();
    if (totalDuration === 0) {
      return (index / videos.length) * 100;
    }
    
    
    const durationSum = videos
      .slice(0, index)
      .reduce((sum, video) => sum + (video.duration || 0), 0);
    
    
    return (durationSum / totalDuration) * 100;
  };

  const handleUndo = async () => {};

  const handleRedo = async () => {};

  const handleTrim = async (args) => {
    let { startTime, endTime } = args;
    // const strtTime: number = trimTime(startTime);
    // const edTime: nullmber = trimTime(endTime);
    

    // const blob: any = await VideoProcessingUtility.trimVideo(videos[index].video, startTime, endTime);//await trimVideo(startTime, endTime, videos[index].video);
    //const blob: any = await handleTrimVideo(videos[index],startTime, endTime);

    onTrimmingChange(videos[index].video,{start:startTime,end:endTime});
  }
  
  const handlePlay = useCallback((value: boolean) => {
    setIsPlaying(value);
    onPlay(value);
  }, []);
  
  


  useEffect(()=>{
    if(index > 0 && index < videos.length -1){
      for(let i = 0; i < index; i++){
        setTotalPlayedDuration(prev=>prev + videos[i]?.duration || 0);
      }
    }
  }, [index]);


  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleThumbnailClick = (thumbnail: any) => {
  };

  const allThumbnails = async (ind: number) => {
    const lframes: any = await getFramesV2(videos[ind]);
    return lframes instanceof Array && lframes.map((frame, index) => (
      {
        src: frame,
        id: `thumbnail-${index}`,
        videoId: `video_${ind}`
      }
    ))

    // return lframes instanceof Array && lframes.map((frame, index) => (
    //     <TrackItemThumbnail key={`frame-${index}`}>
    //         <img src={frame} alt={`Thumbnail ${index}`} />
    //     </TrackItemThumbnail>
    // ));
  };
  
  useEffect(() => {
    
    if (videos?.length > 0 && !isNotPC) {
      
      const fetchThumbnails = async (ind: number) => {

        setLoadingIndex(ind);
        const frames = await allThumbnails(ind);
        setTimeout(() => {}, 1000);
        // setThumbnails(prev => ({ ...prev, [ind]: <>{frames}</> }));
        onThumbNail(frames[0]?.src);
        setThumbnails(prev => ({ ...prev, [ind]: <>
        <VideoThumbnails 
          thumbnails={frames} 
          videoId={"video_"+ind} 
          onThumbnailClick={handleThumbnailClick}
        /></> }));
        setLoadingIndex(null);
      };
      const processVideosSequentially = async () => {
        let j=0
        for (const vid of videos) {
          if (!thumbnails[j]) await fetchThumbnails(j);
          j++;
        }
      };
      processVideosSequentially();
    }

  }, [videos]);
  
  
  useEffect(()=>{
    setTotalDuration(allVideoDuration);
  }, [allVideoDuration])
  
const onTrimPlay=(args)=>{
}
  return (
    <Container>
      <TopBar>
        <IconBox align={"start"}>
          <div style={{borderRight: '1px solid #D4D4D4', height: "100%", display: "flex", alignItems: "center"}}>
            <IconButton>
              <BsListUl size={24} />
            </IconButton>
          </div>
          <IconButton onClick={handleUndo}>
            <GrRevert size={24} />
          </IconButton>
          <IconButton onClick={handleRedo}>
            <GrRedo size={24} />
          </IconButton>
        </IconBox>
        <IconBox align={"center"} style={{margin:"0px auto"}}>
          {isPlaying ? (
            <PlayButton onClick={()=>handlePlay(false)}>
              <BsFillPauseFill size={24} />
            </PlayButton>
          ) : (
            <PlayButton onClick={()=>handlePlay(true)}>
              <BsFillPlayFill size={24} />
            </PlayButton>
          )}
          
          
          <TimeDisplay><span>{formatTime(totalPlayedDuration + progressTracker)}</span> / {formatTime(calculateTotalDuration() || 0)}</TimeDisplay>
        </IconBox>
        <IconBox align={"end"}></IconBox>
      </TopBar>
      
      
       <Timeline 
         progress={totalPlayedDuration + progressTracker}
        min={0} 
        max={calculateTotalDuration()} 
        mainStep={10}
        smallStepCount={4}
      /> 
      <TracksArea>
      
          <Sidebar>
            <ToolButton>
              <FiFileText color={"#000000"} size={24} />
            </ToolButton>
            <ToolButton>
                <BsFilm color={"#000000"} size={24} />
            </ToolButton>
            <ToolButton>
                <FiMusic color={"#000000"} size={24} />
            </ToolButton>
          </Sidebar>
          {/* <button onClick={()=>handleTrimVideoIn()}>Trim Bastard</button> */}
          <div style={{display: "flex", alignItems:"center", gap: "12px"}}>
           {/* <IntroButton>
              <span>Intro</span>
            </IntroButton> */}
            <Tracks>
              {Array.isArray(videos) && videos.map((video, ind) => (
                <VideoTrimmer
                  key={`video-${ind}`}
                  width={calculateCounterPosition(ind)}
                  duration={video.duration}
                  video={video}
                  onTrimChange={handleTrim}
                  onTrimPlay={onTrimPlay}
                  onVideoTap={onVideoTap}
                  index={ind}
                  isActive={index === ind}
                  thumbnails={
                    loadingIndex === ind ? <>Extracting frames...</> : thumbnails[ind] || <>Loading...</>
                  }
                />
              ))}
            </Tracks>
             {/* <OuttroButton>
              <span>Outro +</span>
            </OuttroButton>  */}
          </div>
          
        </TracksArea>
    </Container>
  );
};

export default VideoTimelineEditor;



